import { motion } from 'framer-motion';
import { Instagram, ExternalLink } from 'lucide-react';
import { useEffect, useRef } from 'react';

// Using local hotel images as Instagram-style feed with embed fallback
const instagramPosts = [
  { id: 1, src: '/images/gallery/hotel-3.jpeg', likes: '2.4k' },
  { id: 2, src: '/images/gallery/hotel-5.jpeg', likes: '1.8k' },
  { id: 3, src: '/images/gallery/hotel-8.jpeg', likes: '3.2k' },
  { id: 4, src: '/images/gallery/hotel-10.jpeg', likes: '2.1k' },
  { id: 5, src: '/images/gallery/hotel-15.jpeg', likes: '4.5k' },
  { id: 6, src: '/images/gallery/hotel-22.jpeg', likes: '1.9k' },
];

// Instagram embed component - displays embedded posts when available
function InstagramEmbed() {
  const containerRef = useRef(null);

  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    // Process embeds when script loads
    script.onload = () => {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    };

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
      {/* Instagram Profile Embed */}
      <div className="w-full max-w-[400px]">
        <blockquote 
          className="instagram-media" 
          data-instgrm-permalink="https://www.instagram.com/thehotelemerald/"
          data-instgrm-version="14"
          style={{
            background: '#FFF',
            border: 0,
            borderRadius: '3px',
            boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
            margin: '1px',
            maxWidth: '540px',
            minWidth: '326px',
            padding: 0,
            width: '100%'
          }}
        >
          <div style={{ padding: '16px' }}>
            <a 
              href="https://www.instagram.com/thehotelemerald/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-emerald-600 hover:text-emerald-700"
            >
              View this profile on Instagram
            </a>
          </div>
        </blockquote>
      </div>
    </div>
  );
}

export function InstagramFeed() {
  const instagramHandle = '@thehotelemerald';
  const instagramUrl = 'https://instagram.com/thehotelemerald';

  return (
    <section className="section-padding bg-muted/30" data-testid="instagram-feed-section">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Follow Our Journey</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            <Instagram className="inline-block w-8 h-8 mr-3 text-emerald-700 dark:text-emerald-400" />
            {instagramHandle}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Share your Emerald moments with us and get featured on our feed
          </p>
        </motion.div>

        {/* Instagram Grid - Preview Images */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {instagramPosts.map((post, index) => (
            <motion.a
              key={post.id}
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group relative aspect-square overflow-hidden rounded-sm"
              data-testid={`instagram-post-${post.id}`}
            >
              <img
                src={post.src}
                alt={`Instagram post ${post.id}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-emerald-950/0 group-hover:bg-emerald-950/60 transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center text-white">
                  <Instagram className="w-8 h-8 mx-auto mb-2" />
                  <span className="text-sm font-medium">{post.likes} likes</span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-8"
        >
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold inline-flex items-center gap-2"
            data-testid="follow-instagram-btn"
          >
            <Instagram className="w-4 h-4" />
            Follow Us on Instagram
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
